import { useState } from 'react'
import { Star, Folder, FolderPlus } from 'lucide-react'
import { useBookmarks } from '../contexts/BookmarkContext'
import Popover from './Popover'

interface Props {
  pmcid: string
  title?: string | null
  className?: string
}

// Star toggle next to an article id. Filled = bookmarked. Clicking an unsaved
// star opens a folder picker (with a "new folder" inline create); clicking a
// saved star removes the bookmark.
export default function BookmarkStar({ pmcid, title, className = '' }: Props) {
  const { folders, isBookmarked, addBookmark, removeBookmark, createFolder } = useBookmarks()
  const bookmarked = isBookmarked(pmcid)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [newMode, setNewMode] = useState(false)
  const [newName, setNewName] = useState('')

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (bookmarked) {
      setBusy(true)
      removeBookmark(pmcid)
        .catch((err: any) => alert(err?.response?.data?.detail || 'Failed to remove bookmark'))
        .finally(() => setBusy(false))
    } else {
      setNewMode(false)
      setNewName('')
      setOpen(true)
    }
  }

  const save = async (folderId: string | null) => {
    setBusy(true)
    try {
      await addBookmark(pmcid, title, folderId)
      setOpen(false)
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to add bookmark')
    } finally {
      setBusy(false)
    }
  }

  const createAndSave = async () => {
    const name = newName.trim()
    if (!name) return
    try {
      const folder = await createFolder(name)
      setNewMode(false)
      setNewName('')
      await save(folder.folder_id)
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to create folder')
    }
  }

  return (
    <>
      <button
        onClick={handleClick}
        disabled={busy}
        className={`inline-flex items-center align-middle transition-colors ${
          bookmarked ? 'text-yellow-500 hover:text-yellow-600' : 'text-gray-300 hover:text-yellow-400'
        } ${busy ? 'opacity-50' : ''} ${className}`}
        title={bookmarked ? 'Remove bookmark' : 'Bookmark this article'}
      >
        <Star className="w-3.5 h-3.5" fill={bookmarked ? 'currentColor' : 'none'} />
      </button>

      <Popover open={open} onClose={() => setOpen(false)} width={250}>
        <div className="text-sm">
          <div className="text-xs text-gray-500 px-2 pt-1 pb-2 truncate" title={pmcid}>Save {pmcid} to…</div>
          <div className="max-h-56 overflow-y-auto">
            {folders.map(f => (
              <button
                key={f.folder_id}
                onClick={() => save(f.folder_id)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left"
              >
                <Folder className="w-3.5 h-3.5 text-gray-500" />
                <span className="flex-1 truncate">{f.name}</span>
                {typeof f.bookmark_count === 'number' && (
                  <span className="text-[10px] text-gray-400">{f.bookmark_count}</span>
                )}
              </button>
            ))}
            <button
              onClick={() => save(null)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left"
            >
              <Folder className="w-3.5 h-3.5 text-gray-300" />
              <span className="flex-1 truncate italic text-gray-600">Unsorted</span>
            </button>
          </div>
          <div className="border-t mt-1 pt-1">
            {newMode ? (
              <div className="px-2 py-1.5 flex gap-1">
                <input
                  autoFocus
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') createAndSave()
                    else if (e.key === 'Escape') { setNewMode(false); setNewName('') }
                  }}
                  placeholder="Folder name…"
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                />
                <button
                  onClick={createAndSave}
                  disabled={!newName.trim()}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            ) : (
              <button
                onClick={() => setNewMode(true)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 text-left text-blue-700"
              >
                <FolderPlus className="w-3.5 h-3.5" />
                <span>New folder…</span>
              </button>
            )}
          </div>
        </div>
      </Popover>
    </>
  )
}
